import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import {
  Camera,
  ChevronRight,
  Download,
  Film,
  Plus,
  ExternalLink,
  Square,
  Video,
  VideoOff,
} from "lucide-react";
import DraggablePanel from "./DraggablePanel";
import MutedText from "../ui/MutedText";
import { logger } from "../../utils/logger";
import MenuSelect from "../ui/MenuSelect";
import Tooltip from "../ui/Tooltip";
import { useBodies, useCameras, useSceneActions, useSites } from "../../stores/sceneStore";
import { useObjectRegistry } from "../../runtime/objectRegistry";
import { useCameraProbeMarkerActions } from "../../stores/cameraProbeMarkerStore";
import { getViewerHandles } from "../../runtime/viewerHandles";
import { buildSceneCameraList } from "../../utils/cameraList";
import { nextAvailableCameraName } from "../../utils/nextAvailableCameraName";
import { useSelectionStore } from "../../stores/selectionStore";
import PhysicsAuthoringLockNotice from "../ui/PhysicsAuthoringLockNotice";
import { getPhysicsAuthoringLockMessage, usePhysicsAuthoringLock } from "../../utils/physicsGuards";
import { useIsMobile } from "../../hooks/useIsMobile";
import { dispatchViewportCameraVisibility } from "./scene-inventory/sceneInventoryViewportEvents";

const DEFAULT_CAMERA_WIDTH = 800;
const DEFAULT_CAMERA_HEIGHT = 800;
function safeExportBasename(name, fallback = "camera") {
  const base = String(name || "").trim() || fallback;
  const cleaned = base
    .replace(/[^a-z0-9._-]+/gi, "_")
    .replace(/^\.+/, "")
    .slice(0, 64);
  return cleaned || fallback;
}

function exportTimestamp() {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

function pickWebmMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function")
    return "";
  const candidates = ["video/webm;codecs=vp9", "video/webm;codecs=vp8", "video/webm"];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "";
}

function vec3FromObj(obj) {
  return [Number(obj?.x || 0), Number(obj?.y || 0), Number(obj?.z || 0)];
}

function getBodyWorldPos(sceneObjects, bodyUuid) {
  const obj = sceneObjects?.[bodyUuid] || null;
  if (!obj) return null;
  const v = new THREE.Vector3();
  obj.getWorldPosition(v);
  return vec3FromObj(v);
}

function getBodyWorldQuaternion(sceneObjects, bodyUuid) {
  const obj = sceneObjects?.[bodyUuid] || null;
  if (!obj) return null;
  const q = new THREE.Quaternion();
  obj.getWorldQuaternion(q);
  return q;
}

function getSiteWorldPos({ sceneObjects, bodies, sites, siteUuid }) {
  if (!siteUuid) return null;
  const site = sites?.[siteUuid] || null;
  if (!site || !Array.isArray(site.position) || site.position.length < 3) return null;
  const parentBodyUuid =
    Object.values(bodies || {}).find((b) => Array.isArray(b?.sites) && b.sites.includes(siteUuid))
      ?.uuid || null;
  if (!parentBodyUuid) return null;
  const bodyObj = sceneObjects?.[parentBodyUuid] || null;
  if (!bodyObj) return null;
  const v = new THREE.Vector3(site.position[0], site.position[1], site.position[2]);
  bodyObj.localToWorld(v);
  return vec3FromObj(v);
}

function getSiteWorldQuaternion({ sceneObjects, bodies, sites, siteUuid }) {
  if (!siteUuid) return null;
  const site = sites?.[siteUuid] || null;
  const parentBodyUuid =
    Object.values(bodies || {}).find((b) => Array.isArray(b?.sites) && b.sites.includes(siteUuid))
      ?.uuid || null;
  if (!parentBodyUuid) return null;
  const bodyQuat = getBodyWorldQuaternion(sceneObjects, parentBodyUuid);
  if (!bodyQuat) return null;
  const s =
    Array.isArray(site?.orientation) && site.orientation.length === 4
      ? site.orientation
      : [1, 0, 0, 0];
  const siteLocalQuat = new THREE.Quaternion(
    Number(s[1]) || 0,
    Number(s[2]) || 0,
    Number(s[3]) || 0,
    Number(s[0]) || 1
  );
  return bodyQuat.clone().multiply(siteLocalQuat);
}

const EMPTY_OBJECT = Object.freeze({});

const CameraProbePanel = ({ open, onClose, titleIcon, mobilePinPanelId }) => {
  const isMobile = useIsMobile();
  const sceneObjects = useObjectRegistry() ?? EMPTY_OBJECT;
  const bodies = useBodies() ?? EMPTY_OBJECT;
  const sites = useSites() ?? EMPTY_OBJECT;
  const savedCameras = useCameras() ?? EMPTY_OBJECT;
  const sceneActions = useSceneActions();
  const authoringLocked = usePhysicsAuthoringLock();
  const storeSelectedCamera = useSelectionStore((s) => s.selectedCamera);
  const selectCamera = useSelectionStore((s) => s.selectCamera);
  const setLastSelectionSource = useSelectionStore((s) => s.setLastSelectionSource);
  const markerActions = useCameraProbeMarkerActions();

  const [localSelectedUuid, setLocalSelectedUuid] = useState("");
  const [streamOn, setStreamOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [lastError, setLastError] = useState("");
  const [imageSrc, setImageSrc] = useState("");
  const [inspectorMinimized, setInspectorMinimized] = useState(true);
  const cameraLockMessage = getPhysicsAuthoringLockMessage("create cameras");
  const cameraLockTitle = authoringLocked ? cameraLockMessage : undefined;

  const tickRef = useRef(null);
  const captureCameraRef = useRef(null);
  const captureRendererRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const captureBlobUrlRef = useRef("");
  const captureEncodeSeqRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  /** Last camera uuid we captured a still preview for while the panel is open; reset when panel closes. */
  const lastSnapshotCameraUuidRef = useRef(null);
  const localSelectedUuidRef = useRef("");
  const openRef = useRef(open);

  const sortedCameraPresets = useMemo(
    () =>
      buildSceneCameraList(savedCameras)
        .map((row) => savedCameras?.[row.uuid])
        .filter(Boolean),
    [savedCameras]
  );

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    const onInspectorMinimized = (event) => {
      const next = event?.detail?.minimized;
      if (typeof next === "boolean") setInspectorMinimized(next);
    };
    window.addEventListener("properties-panel-minimized-change", onInspectorMinimized);
    window.dispatchEvent(new CustomEvent("request-properties-panel-minimized-state"));
    return () =>
      window.removeEventListener("properties-panel-minimized-change", onInspectorMinimized);
  }, []);

  useEffect(() => {
    localSelectedUuidRef.current = localSelectedUuid;
  }, [localSelectedUuid]);

  // Sync local selection from store when store changes externally
  useEffect(() => {
    if (storeSelectedCamera && savedCameras?.[storeSelectedCamera]) {
      setLocalSelectedUuid(storeSelectedCamera);
    }
  }, [storeSelectedCamera, savedCameras]);

  // Auto-select first camera when panel opens or cameras list changes
  useEffect(() => {
    if (!open) return;
    if (localSelectedUuid && savedCameras?.[localSelectedUuid]) return;
    if (sortedCameraPresets.length > 0) {
      setLocalSelectedUuid(sortedCameraPresets[0].uuid);
    } else {
      setLocalSelectedUuid("");
    }
  }, [open, localSelectedUuid, savedCameras, sortedCameraPresets]);

  const cam = localSelectedUuid ? savedCameras?.[localSelectedUuid] : null;

  const getCurrentCameraWorldPose = () => {
    if (!cam) return { worldPos: new THREE.Vector3(0, 0.5, 2), worldQuat: new THREE.Quaternion() };
    const pos = Array.isArray(cam.position) ? cam.position : [0, 0.5, 2];
    const quat =
      Array.isArray(cam.orientation) && cam.orientation.length === 4
        ? cam.orientation
        : [1, 0, 0, 0];
    const localQuat = new THREE.Quaternion(
      Number(quat[1]) || 0,
      Number(quat[2]) || 0,
      Number(quat[3]) || 0,
      Number(quat[0]) || 1
    );
    const localPos = new THREE.Vector3(
      Number(pos[0] || 0),
      Number(pos[1] || 0),
      Number(pos[2] || 0)
    );

    if (cam.kind !== "mounted") {
      return { worldPos: localPos, worldQuat: localQuat };
    }

    const sourcePos =
      cam.mountType === "site"
        ? getSiteWorldPos({ sceneObjects, bodies, sites, siteUuid: cam.mountSiteUuid })
        : getBodyWorldPos(sceneObjects, cam.mountBodyUuid);
    const sourceQuat =
      cam.mountType === "site"
        ? getSiteWorldQuaternion({ sceneObjects, bodies, sites, siteUuid: cam.mountSiteUuid })
        : getBodyWorldQuaternion(sceneObjects, cam.mountBodyUuid);

    const worldQuat = (sourceQuat || new THREE.Quaternion()).clone().multiply(localQuat);
    if (!sourcePos) return { worldPos: localPos, worldQuat };
    const worldPos = new THREE.Vector3(sourcePos[0], sourcePos[1], sourcePos[2]);
    worldPos.add(localPos.clone().applyQuaternion(sourceQuat || new THREE.Quaternion()));
    return { worldPos, worldQuat };
  };

  const ensureCaptureCamera = (w, h) => {
    const safeW = Math.max(1, w);
    const safeH = Math.max(1, h);
    const nextFov = Math.max(10, Math.min(150, Number(cam?.fovy) || 65));
    if (!captureCameraRef.current) {
      captureCameraRef.current = new THREE.PerspectiveCamera(nextFov, safeW / safeH, 0.02, 1000);
      return captureCameraRef.current;
    }
    const c = captureCameraRef.current;
    c.fov = nextFov;
    c.aspect = safeW / safeH;
    c.updateProjectionMatrix();
    return c;
  };

  const ensureCaptureRenderer = (w, h, sourceRenderer) => {
    let renderer = captureRendererRef.current;
    if (!renderer) {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        failIfMajorPerformanceCaveat: false,
        preserveDrawingBuffer: true,
      });
      renderer.setPixelRatio(1);
      renderer.domElement.style.display = "none";
      renderer.shadowMap.enabled = true;
      captureRendererRef.current = renderer;
      captureCanvasRef.current = renderer.domElement;
    }

    renderer.setSize(w, h, false);
    renderer.shadowMap.enabled = sourceRenderer?.shadowMap?.enabled ?? true;
    renderer.shadowMap.type = sourceRenderer?.shadowMap?.type ?? THREE.PCFSoftShadowMap;
    renderer.toneMapping = sourceRenderer?.toneMapping ?? THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = sourceRenderer?.toneMappingExposure ?? 1.2;
    renderer.outputColorSpace = sourceRenderer?.outputColorSpace ?? THREE.SRGBColorSpace;
    return renderer;
  };

  const publishFrame = (canvas) => {
    const seq = captureEncodeSeqRef.current + 1;
    captureEncodeSeqRef.current = seq;
    if (typeof canvas.toBlob !== "function") {
      setImageSrc(canvas.toDataURL("image/png"));
      return;
    }
    canvas.toBlob((blob) => {
      if (captureEncodeSeqRef.current !== seq || !blob) return;
      const nextUrl = URL.createObjectURL(blob);
      const prevUrl = captureBlobUrlRef.current;
      captureBlobUrlRef.current = nextUrl;
      setImageSrc(nextUrl);
      if (prevUrl) URL.revokeObjectURL(prevUrl);
    }, "image/png");
  };

  const captureFrame = () => {
    try {
      const { scene, renderer: sourceRenderer } = getViewerHandles();
      if (!sourceRenderer || !scene) {
        setLastError("Three renderer/scene not available yet.");
        return;
      }

      const w = Math.max(32, Math.floor(Number(cam?.width) || DEFAULT_CAMERA_WIDTH));
      const h = Math.max(32, Math.floor(Number(cam?.height) || DEFAULT_CAMERA_HEIGHT));
      const c = ensureCaptureCamera(w, h);
      const pose = getCurrentCameraWorldPose();
      c.position.copy(pose.worldPos);
      c.quaternion.copy(pose.worldQuat);
      c.updateMatrixWorld(true);

      const captureRenderer = ensureCaptureRenderer(w, h, sourceRenderer);
      captureRenderer.render(scene, c);
      publishFrame(captureRenderer.domElement);
      setLastError("");
    } catch (e) {
      setLastError(String(e?.message || e));
    }
  };

  const saveScreenshotPng = () => {
    try {
      let canvas = captureCanvasRef.current;
      if (!canvas || canvas.width < 32 || canvas.height < 32) {
        captureFrame();
        canvas = captureCanvasRef.current;
      }
      if (!canvas || canvas.width < 32 || canvas.height < 32) {
        setLastError("Capture a frame first.");
        return;
      }
      const safe = safeExportBasename(cam?.name, "camera");
      const filename = `${safe}_${exportTimestamp()}.png`;
      if (typeof canvas.toBlob === "function") {
        canvas.toBlob((blob) => {
          if (!blob) {
            setLastError("Failed to encode PNG.");
            return;
          }
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          a.click();
          URL.revokeObjectURL(url);
          setLastError("");
        }, "image/png");
      } else {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = filename;
        a.click();
        setLastError("");
      }
    } catch (e) {
      setLastError(String(e?.message || e));
    }
  };

  const startVideoRecording = () => {
    try {
      captureFrame();
      const canvas = captureCanvasRef.current;
      if (!canvas || canvas.width < 32 || canvas.height < 32) {
        setLastError("Could not start recording.");
        return;
      }
      const mimeType = pickWebmMimeType();
      if (!mimeType) {
        setLastError("Video export needs WebM recording support in your browser.");
        return;
      }
      const hz = Math.max(1, Math.min(30, Math.floor(Number(cam?.fps) || 8)));
      const stream = canvas.captureStream(hz);
      recordChunksRef.current = [];
      const rec = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 4_000_000 });
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      rec.onerror = () => {
        setLastError("Recording stopped due to a browser error.");
        setIsRecording(false);
        mediaRecorderRef.current = null;
      };
      mediaRecorderRef.current = rec;
      rec.start(250);
      setIsRecording(true);
      setLastError("");
    } catch (e) {
      setLastError(String(e?.message || e));
    }
  };

  const stopVideoRecording = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === "inactive") {
      setIsRecording(false);
      mediaRecorderRef.current = null;
      return;
    }
    const mimeType = rec.mimeType || "video/webm";
    rec.onstop = () => {
      const chunks = recordChunksRef.current;
      recordChunksRef.current = [];
      mediaRecorderRef.current = null;
      setIsRecording(false);
      if (!chunks.length) {
        setLastError("No video data was recorded.");
        return;
      }
      const blob = new Blob(chunks, { type: mimeType });
      const safe = safeExportBasename(cam?.name, "camera");
      const filename = `${safe}_${exportTimestamp()}.webm`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setLastError("");
    };
    try {
      rec.stop();
    } catch (e) {
      setLastError(String(e?.message || e));
      setIsRecording(false);
      mediaRecorderRef.current = null;
    }
  };

  // Update camera position marker
  useEffect(() => {
    if (!open || !cam) return;
    const pose = getCurrentCameraWorldPose();
    const world = [pose.worldPos.x, pose.worldPos.y, pose.worldPos.z];
    markerActions.setMarkerWorld(world);
    window.dispatchEvent(new CustomEvent("camera-probe-marker-world", { detail: { world } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cam, sceneObjects, bodies, sites, markerActions]);

  // If the selected probe camera disappears (e.g. body delete cascades camera removal),
  // clear the probe marker so a stale marker doesn't stay in the scene.
  useEffect(() => {
    if (!open || cam) return;
    markerActions.setMarkerVisible(false);
    markerActions.clearMarkerWorld();
    window.dispatchEvent(
      new CustomEvent("camera-probe-marker-visibility", { detail: { visible: false } })
    );
    window.dispatchEvent(new CustomEvent("camera-probe-marker-clear"));
  }, [open, cam, markerActions]);

  useEffect(() => {
    if (open) return;
    markerActions.setMarkerVisible(false);
    window.dispatchEvent(
      new CustomEvent("camera-probe-marker-visibility", { detail: { visible: false } })
    );
  }, [open, markerActions]);

  useEffect(() => {
    if (!open) {
      lastSnapshotCameraUuidRef.current = null;
    }
  }, [open]);

  // When the inspector requests the probe via "Open Camera Probe" while this panel is already open,
  // bump the probe to that camera without calling selectCamera if the store already has it (would toggle off).
  useEffect(() => {
    const handleOpenProbeRequest = (event) => {
      const uuid = event?.detail?.cameraUuid;
      if (!open || typeof uuid !== "string" || !uuid || !savedCameras?.[uuid]) return;
      if (uuid === localSelectedUuidRef.current) return;

      const storeCam = useSelectionStore.getState().selectedCamera;
      if (storeCam !== uuid) {
        setLastSelectionSource("sceneTree");
        selectCamera(uuid);
      }
      setLocalSelectedUuid(uuid);
    };
    window.addEventListener("open-camera-probe", handleOpenProbeRequest);
    return () => window.removeEventListener("open-camera-probe", handleOpenProbeRequest);
  }, [open, savedCameras, selectCamera, setLastSelectionSource]);

  // Still preview when opening the probe or when the active probe camera changes.
  useEffect(() => {
    if (!open || !cam || !localSelectedUuid) return;
    if (lastSnapshotCameraUuidRef.current === localSelectedUuid) return;
    const targetUuid = localSelectedUuid;
    const frameId = requestAnimationFrame(() => {
      if (!openRef.current || localSelectedUuidRef.current !== targetUuid) return;
      captureFrame();
      lastSnapshotCameraUuidRef.current = targetUuid;
    });
    return () => cancelAnimationFrame(frameId);
    // captureFrame intentionally omitted (uses latest cam/renderer each open)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, localSelectedUuid, cam]);

  // Stream ticker
  useEffect(() => {
    if ((!streamOn && !isRecording) || !open || !cam) return;
    const hz = Math.max(1, Math.min(30, Math.floor(Number(cam?.fps) || 8)));
    const ms = Math.floor(1000 / hz);
    tickRef.current = window.setInterval(captureFrame, ms);
    captureFrame();
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      tickRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamOn, isRecording, open, localSelectedUuid, cam]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== "inactive") {
        try {
          rec.stop();
        } catch {
          /* ignore */
        }
        logger.warn("[CameraProbePanel] Panel closed while recording; video was not saved.");
      }
      mediaRecorderRef.current = null;
      recordChunksRef.current = [];
      if (tickRef.current) window.clearInterval(tickRef.current);
      if (captureRendererRef.current) {
        captureRendererRef.current.forceContextLoss();
        captureRendererRef.current.dispose();
        captureRendererRef.current = null;
      }
      captureCameraRef.current = null;
      captureCanvasRef.current = null;
      if (captureBlobUrlRef.current) {
        URL.revokeObjectURL(captureBlobUrlRef.current);
        captureBlobUrlRef.current = "";
      }
    };
  }, []);

  const handleProbeCameraChange = (uuid) => {
    setLocalSelectedUuid(uuid);
    if (uuid) {
      setLastSelectionSource("sceneTree");
      selectCamera(uuid);
    }
  };

  const inspectorShowingThisProbeCamera =
    !inspectorMinimized && !!localSelectedUuid && storeSelectedCamera === localSelectedUuid;

  const handleInspectorShortcutClick = () => {
    if (inspectorShowingThisProbeCamera) {
      window.dispatchEvent(new CustomEvent("close-properties-panel"));
      return;
    }
    if (!localSelectedUuid) return;
    setLastSelectionSource("sceneTree");
    // selectCamera toggles off when uuid matches — after "close inspector" the camera stays
    // selected, so re-calling select would clear it and break open/close (multi-click churn).
    if (storeSelectedCamera !== localSelectedUuid) {
      selectCamera(localSelectedUuid);
    }
    window.dispatchEvent(new CustomEvent("open-properties-panel"));
  };

  const handleCreateCamera = () => {
    if (authoringLocked) return;
    const name = nextAvailableCameraName(savedCameras, { kind: "world" });
    const newUuid = sceneActions.addCamera({
      name,
      kind: "fixed",
      position: [0, 0.5, 2],
      orientation: [1, 0, 0, 0],
      fovy: 65,
      width: DEFAULT_CAMERA_WIDTH,
      height: DEFAULT_CAMERA_HEIGHT,
      fps: 8,
    });
    if (newUuid) {
      dispatchViewportCameraVisibility(true);
      setLocalSelectedUuid(newUuid);
      setLastSelectionSource("sceneTree");
      selectCamera(newUuid);
    }
  };

  const handleClose = () => {
    markerActions.setMarkerVisible(false);
    markerActions.clearMarkerWorld();
    window.dispatchEvent(
      new CustomEvent("camera-probe-marker-visibility", { detail: { visible: false } })
    );
    window.dispatchEvent(new CustomEvent("camera-probe-marker-clear"));
    if (typeof onClose === "function") onClose();
  };

  return (
    <DraggablePanel
      title="Camera Probe"
      titleIcon={titleIcon}
      open={open}
      onClose={handleClose}
      initialSize={{ width: 420, height: 420 }}
      minWidth={320}
      minHeight={280}
      mobilePinPanelId={mobilePinPanelId}
      rootExtraClassName={isMobile ? "draggable-panel--mobile-autosize" : ""}
    >
      <div
        className={
          isMobile
            ? "flex w-full flex-col gap-3 p-3 text-sm text-gray-100"
            : "flex h-full w-full flex-col gap-3 overflow-y-auto p-3 text-sm text-gray-100"
        }
      >
        <PhysicsAuthoringLockNotice actionText={cameraLockMessage} />

        <div className="flex items-center gap-2">
          <MenuSelect
            size="compact"
            className="flex-1 min-w-0"
            value={localSelectedUuid}
            onChange={handleProbeCameraChange}
            disabled={sortedCameraPresets.length === 0}
            placeholder={sortedCameraPresets.length === 0 ? "No cameras" : "Select camera…"}
            options={
              sortedCameraPresets.length === 0
                ? []
                : sortedCameraPresets.map((preset) => ({
                    value: preset.uuid,
                    label: preset.name || preset.uuid,
                  }))
            }
            contentClassName="max-h-[min(280px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Camera to probe"
          />
          <Tooltip
            content={inspectorShowingThisProbeCamera ? "Close inspector" : "Open in Inspector"}
          >
            <button
              type="button"
              aria-label={inspectorShowingThisProbeCamera ? "Close inspector" : "Open in Inspector"}
              disabled={!inspectorShowingThisProbeCamera && !localSelectedUuid}
              onClick={handleInspectorShortcutClick}
              className="flex items-center justify-center h-7 w-7 rounded border border-gray-700 text-gray-300 hover:text-gray-100 hover:border-gray-500 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              {inspectorShowingThisProbeCamera ? (
                <ChevronRight className="w-3.5 h-3.5" aria-hidden />
              ) : (
                <ExternalLink className="w-3.5 h-3.5" aria-hidden />
              )}
            </button>
          </Tooltip>
          <Tooltip content={cameraLockTitle || "New camera"}>
            <button
              type="button"
              aria-label="New camera"
              disabled={authoringLocked}
              onClick={handleCreateCamera}
              className="flex items-center justify-center h-7 w-7 rounded border border-gray-700 text-gray-300 hover:text-gray-100 hover:border-gray-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-700 disabled:hover:text-gray-300 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>

        {cam ? (
          <div className="border border-gray-700 rounded-md p-2 space-y-2 bg-black/40">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Camera probe stream"
                className="w-full h-auto rounded border border-gray-700"
              />
            ) : (
              <MutedText>No frame captured yet.</MutedText>
            )}
            <div className="flex items-center gap-2">
              <Tooltip content={streamOn ? "Stop stream" : "Start stream"}>
                <button
                  className={`px-3 py-1 rounded border disabled:opacity-50 ${
                    streamOn
                      ? "border-green-600 bg-green-900/40 text-green-200 hover:bg-green-900/60"
                      : "border-gray-600 bg-gray-900/20 text-gray-300 hover:bg-gray-700"
                  }`}
                  onClick={() => setStreamOn((v) => !v)}
                  aria-label={streamOn ? "Stop stream" : "Start stream"}
                  aria-pressed={streamOn}
                >
                  {streamOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </button>
              </Tooltip>
              <Tooltip content="Capture once">
                <button
                  type="button"
                  className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
                  onClick={captureFrame}
                  aria-label="Capture once"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </Tooltip>
              <Tooltip content="Save preview as PNG">
                <button
                  type="button"
                  className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
                  onClick={saveScreenshotPng}
                  aria-label="Save preview as PNG"
                >
                  <Download className="w-4 h-4" />
                </button>
              </Tooltip>
              <Tooltip
                content={
                  isRecording ? "Stop and download WebM" : "Record video (WebM) at stream FPS"
                }
              >
                <button
                  type="button"
                  className={`px-3 py-1 rounded border disabled:opacity-50 ${
                    isRecording
                      ? "border-red-600 bg-red-900/40 text-red-200 hover:bg-red-900/55"
                      : "border-gray-600 bg-gray-900/20 text-gray-300 hover:bg-gray-700"
                  }`}
                  onClick={() => (isRecording ? stopVideoRecording() : startVideoRecording())}
                  aria-label={isRecording ? "Stop recording" : "Start recording video"}
                  aria-pressed={isRecording}
                >
                  {isRecording ? <Square className="w-4 h-4" /> : <Film className="w-4 h-4" />}
                </button>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div
            className={
              isMobile
                ? "flex flex-col items-center justify-center py-8 text-gray-400"
                : "flex flex-1 items-center justify-center py-8 text-gray-400"
            }
          >
            <div className="text-center">
              <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No camera selected</p>
            </div>
          </div>
        )}

        {lastError ? (
          <div className="text-xs text-red-300 whitespace-pre-wrap">{lastError}</div>
        ) : null}
      </div>
    </DraggablePanel>
  );
};

export default React.memo(CameraProbePanel);
